import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { validarCPF, validarCNPJ } from '../../../../src/lib/utils';
import { ORIGENS_NEGOCIACAO } from '../../../../src/lib/origensNegociacao';
import { type DadosPerguntaPagador } from './PerguntaPagador';
import { nomeEtapaNaTela } from '../../../../src/lib/nomeEtapaNaTela';

interface StepBasicInfoProps {
    data: {
        nome_cliente: string;
        whatsapp: string;
        cpf_cnpj: string;
        email: string;
        origem?: string;
        plataforma?: string;
        campanha?: string;
        anuncio?: string;
        formulario?: string;
        reside_goias?: boolean | null;
        associateUC: boolean;
        wantDocuments: boolean;
        stage?: string;
        status?: string;
        cpf_representante?: string;
        nome_representante_legal?: string;
        contato_financeiro?: string;
        email_financeiro?: string;
        whatsapp_financeiro?: string;
        contrato_social_data_versao?: string;
    } & DadosPerguntaPagador;
    pipelineStages: any[];
    tipo: 'PF' | 'PJ';
    origemObrigatoria?: boolean;
    onChange: (field: string, value: any) => void;
    onNext: () => void;
    onBack: () => void;
    onFinish: () => void; // Called if associateUC is false
    loading?: boolean;
}

const StepBasicInfo: React.FC<StepBasicInfoProps> = ({ data, pipelineStages, tipo, origemObrigatoria, onChange, onNext, onBack, onFinish, loading }) => {
    const [error, setError] = useState<string>('');

    const ORIGEM_OPTIONS = ORIGENS_NEGOCIACAO;

    // Simple mask helpers (basic implementation)
    const maskPhone = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/^(\d{2})(\d)/g, '($1) $2')
            .replace(/(\d)(\d{4})$/, '$1-$2')
            .slice(0, 15);
    };

    const maskCPF = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    };

    const maskCNPJ = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/^(\d{2})(\d)/, '$1.$2')
            .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/\.(\d{3})(\d)/, '.$1/$2')
            .replace(/(\d{4})(\d)/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    };

    const handleNext = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (data.cpf_cnpj) {
            if (tipo === 'PF' && !validarCPF(data.cpf_cnpj)) {
                setError('CPF inválido.');
                return;
            }
            if (tipo === 'PJ' && !validarCNPJ(data.cpf_cnpj)) {
                setError('CNPJ inválido.');
                return;
            }
        }
        if (tipo === 'PJ' && data.cpf_representante && !validarCPF(data.cpf_representante)) {
            setError('CPF do representante inválido.');
            return;
        }
        if (origemObrigatoria && !data.origem) {
            setError('Selecione a origem do lead.');
            return;
        }
        if (data.associateUC) {
            onNext();
        } else if (data.wantDocuments) {
            onNext();
        } else {
            onFinish();
        }
    };

    return (
        <form onSubmit={handleNext} className="flex flex-col gap-4 animate-fade-in">
            <div className="text-center mb-2">
                <h3 className="text-lg font-bold text-gray-800 dark:text-slate-100">Dados cadastrais</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400">Preencha as informações do cliente ({tipo}).</p>
                {error && <p className="text-sm text-red-500 mt-2 font-semibold">{error}</p>}
            </div>

            <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                    {tipo === 'PF' ? 'Nome Completo' : 'Razão Social'} <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    required
                    value={data.nome_cliente}
                    onChange={(e) => onChange('nome_cliente', e.target.value)}
                    className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                    placeholder="Nome do cliente"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        WhatsApp <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="tel"
                        required
                        value={data.whatsapp}
                        onChange={(e) => onChange('whatsapp', maskPhone(e.target.value))}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                        placeholder="(00) 00000-0000"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        {tipo === 'PF' ? 'CPF' : 'CNPJ'}
                    </label>
                    <input
                        type="text"
                        value={data.cpf_cnpj}
                        onChange={(e) => onChange('cpf_cnpj', tipo === 'PF' ? maskCPF(e.target.value) : maskCNPJ(e.target.value))}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                        placeholder={tipo === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        E-mail
                    </label>
                    <input
                        type="email"
                        value={data.email}
                        onChange={(e) => onChange('email', e.target.value)}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                        placeholder="email@exemplo.com"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        Origem {origemObrigatoria && <span className="text-red-500">*</span>}
                    </label>
                    <select
                        required={origemObrigatoria}
                        value={data.origem || ''}
                        onChange={(e) => onChange('origem', e.target.value)}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                    >
                        <option value="">Selecione a origem...</option>
                        {ORIGEM_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        Plataforma/Canal
                    </label>
                    <input
                        type="text"
                        value={data.plataforma || ''}
                        onChange={(e) => onChange('plataforma', e.target.value)}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                        placeholder="Ex: Facebook, Instagram, Google"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        Campanha
                    </label>
                    <input
                        type="text"
                        value={data.campanha || ''}
                        onChange={(e) => onChange('campanha', e.target.value)}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                        placeholder="Nome da campanha"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        Anúncio
                    </label>
                    <input
                        type="text"
                        value={data.anuncio || ''}
                        onChange={(e) => onChange('anuncio', e.target.value)}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                        placeholder="Nome do anúncio"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        Formulário
                    </label>
                    <input
                        type="text"
                        value={data.formulario || ''}
                        onChange={(e) => onChange('formulario', e.target.value)}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                        placeholder="Nome do formulário"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        Reside em Goiás?
                    </label>
                    <select
                        value={data.reside_goias === true ? 'sim' : data.reside_goias === false ? 'nao' : ''}
                        onChange={(e) => {
                            const v = e.target.value;
                            onChange('reside_goias', v === 'sim' ? true : v === 'nao' ? false : null);
                        }}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                    >
                        <option value="">Não informado</option>
                        <option value="sim">Sim</option>
                        <option value="nao">Não</option>
                    </select>
                </div>
            </div>

            {tipo === 'PJ' && (
                <>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                                Nome do Representante Legal
                            </label>
                            <input
                                type="text"
                                value={data.nome_representante_legal || ''}
                                onChange={(e) => onChange('nome_representante_legal', e.target.value)}
                                className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                                placeholder="Nome completo do representante"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                                CPF do Representante
                            </label>
                            <input
                                type="text"
                                value={data.cpf_representante || ''}
                                onChange={(e) => onChange('cpf_representante', maskCPF(e.target.value))}
                                className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                                placeholder="000.000.000-00"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                            Contrato Social e Alterações (data/versão)
                        </label>
                        <input
                            type="text"
                            value={data.contrato_social_data_versao || ''}
                            onChange={(e) => onChange('contrato_social_data_versao', e.target.value)}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                            placeholder="Ex: 3ª alteração, de 12/05/2024"
                        />
                    </div>
                </>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        Etapa (Stage)
                    </label>
                    <select
                        value={data.stage}
                        onChange={(e) => onChange('stage', e.target.value)}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                    >
                        {pipelineStages.map(stage => (
                            <option key={stage.id} value={stage.name}>
                                {nomeEtapaNaTela(stage.name)}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        Status
                    </label>
                    <select
                        value={data.status}
                        onChange={(e) => onChange('status', e.target.value)}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                    >
                        <option value="em_andamento">Em andamento</option>
                        <option value="simulacao_realizada">Simulação realizada</option>
                        <option value="termo_enviado">Termo enviado</option>
                        <option value="termo_assinado">Termo assinado</option>
                        <option value="termo_analise">Termo em análise</option>
                        <option value="termo_aprovado">Termo aprovado</option>
                        <option value="termo_reprovado">Termo reprovado</option>
                        <option value="ganho">Ganho</option>
                        <option value="perdido">Perdido</option>
                        <option value="em_pausa">Em pausa</option>
                    </select>
                </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-700/50 mt-2">
                <div className="relative inline-block w-10 h-6 align-middle select-none transition duration-200 ease-in">
                    <input
                        type="checkbox"
                        checked={data.associateUC}
                        onChange={(e) => onChange('associateUC', e.target.checked)}
                        className="toggle-checkbox absolute block w-4 h-4 rounded-full bg-white border-4 appearance-none cursor-pointer translate-x-1 top-1 transition-transform checked:translate-x-5 checked:border-primary"
                        style={{ right: 'unset' }}
                    />
                    <div onClick={() => onChange('associateUC', !data.associateUC)} className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer transition-colors ${data.associateUC ? 'bg-primary' : 'bg-gray-300 dark:bg-slate-600'}`}></div>
                </div>
                <label onClick={() => onChange('associateUC', !data.associateUC)} className="text-sm font-medium text-gray-700 dark:text-slate-300 cursor-pointer select-none">
                    Deseja associar uma Unidade Consumidora (UC) agora?
                </label>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-700/50 mt-2">
                <div className="relative inline-block w-10 h-6 align-middle select-none transition duration-200 ease-in">
                    <input
                        type="checkbox"
                        checked={data.wantDocuments}
                        onChange={(e) => onChange('wantDocuments', e.target.checked)}
                        className="toggle-checkbox absolute block w-4 h-4 rounded-full bg-white border-4 appearance-none cursor-pointer translate-x-1 top-1 transition-transform checked:translate-x-5 checked:border-primary"
                        style={{ right: 'unset' }}
                    />
                    <div onClick={() => onChange('wantDocuments', !data.wantDocuments)} className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer transition-colors ${data.wantDocuments ? 'bg-primary' : 'bg-gray-300 dark:bg-slate-600'}`}></div>
                </div>
                <label onClick={() => onChange('wantDocuments', !data.wantDocuments)} className="text-sm font-medium text-gray-700 dark:text-slate-300 cursor-pointer select-none">
                    Deseja anexar os documentos do cliente agora?
                </label>
            </div>

            <div className="flex justify-between mt-4">
                <button
                    type="button"
                    onClick={onBack}
                    className="px-6 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                    Voltar
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20 flex items-center gap-2"
                >
                    {loading ? 'Salvando...' : ((data.associateUC || data.wantDocuments) ? <>Próximo <ArrowRight size={18} /></> : 'Concluir Cadastro')}
                </button>
            </div>
        </form >
    );
};

export default StepBasicInfo;
